import React, { useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../../../components/Dashboard/DashboardLayout';
import PageHeader from '../../../components/ui/PageHeader';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Skeleton, ErrorState } from '../../../components/ui/Skeleton';
import { useQuery, STALE } from '../../../hooks/useQuery';
import { projektExamAPI } from '../../../services/api';
import { cn } from '../../../lib/utils';
import {
  LESEN_TEILE,
  MODELLTESTS,
  SOURCE_BOOK,
  getTeil,
  gradeTeil,
  pageImageUrl,
} from '../../../data/exam/projekt-b2-neu';
import {
  BookOpen,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RotateCw,
} from 'lucide-react';

const useAttempts = () =>
  useQuery('projekt_attempts', 'list', projektExamAPI.listAttempts, { staleTime: STALE.medium });

// Best score per "test-teil" from the user's own attempts.
const bestByTeil = (attempts = []) => {
  const map = {};
  attempts.forEach((a) => {
    const k = `${a.details.modelltest}-${a.details.teil}`;
    if (!map[k] || (a.score ?? 0) > (map[k].score ?? 0)) map[k] = a;
  });
  return map;
};

// URLs: /exam/b2/lesen → Teil 1–5, /exam/b2/lesen/teil/:teil → Modul 1–15, …/modul/:test → the PDF pages.
export const teilPath = (teil) => `/exam/b2/lesen/teil/${teil}`;
export const modulPath = (teil, test) => `${teilPath(teil)}/modul/${test}`;

const ScoreBadge = ({ attempt }) =>
  attempt ? (
    <span
      className={cn(
        'shrink-0 text-xs font-extrabold',
        attempt.score >= 60 ? 'text-emerald-700' : 'text-zinc-500'
      )}
    >
      {attempt.details.gradedTotal ? `${attempt.score}%` : '✓'}
    </span>
  ) : null;

const cardClass =
  'group flex flex-col justify-between overflow-hidden rounded-2xl border border-zinc-200/80 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-[#DD0000]/40 hover:shadow-md';

// ---------------------------------------------------------------- Teil 1–5
export const ProjektLesenOverview = () => {
  const { data: attempts, error, refetch } = useAttempts();
  const best = useMemo(() => bestByTeil(attempts), [attempts]);

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-[1440px] space-y-6 px-1 pb-10 font-sans sm:px-3">
        <PageHeader
          icon={BookOpen}
          badge="Goethe-Zertifikat B2 · Lesen"
          title="Lesen"
          description={`Zgjidh një Teil. Secili Teil ka ${MODELLTESTS.length} module nga ${SOURCE_BOOK.title}.`}
        />

        {error && <ErrorState className="mb-4" message="Rezultatet e mëparshme nuk u ngarkuan." onRetry={refetch} />}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {LESEN_TEILE.map((t) => {
            const done = MODELLTESTS.filter((mt) => best[`${mt.number}-${t.teil}`]).length;
            return (
              <Link key={t.teil} to={teilPath(t.teil)} className={cardClass}>
                <div>
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <h2 className="text-lg font-extrabold text-zinc-900 group-hover:text-[#DD0000] transition">Teil {t.teil}</h2>
                    <span className="rounded-full border border-zinc-200 bg-zinc-50 px-2.5 py-1 text-[10px] font-extrabold text-zinc-600">
                      ca. {t.minutes} Min.
                    </span>
                  </div>
                  <p className="mt-0.5 text-sm text-zinc-500">{t.label}</p>
                  <p className="mt-1 text-[11px] font-bold text-zinc-400">Aufgaben {t.first}–{t.last}</p>
                  <div
                    role="progressbar"
                    aria-label={`Teil ${t.teil}`}
                    aria-valuemin={0}
                    aria-valuemax={MODELLTESTS.length}
                    aria-valuenow={done}
                    className="mt-4 h-1.5 overflow-hidden rounded-full bg-zinc-100"
                  >
                    <div className="h-full rounded-full bg-[#DD0000] transition-[width]" style={{ width: `${(done / MODELLTESTS.length) * 100}%` }} />
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-between border-t border-zinc-100 pt-3 text-xs font-bold text-zinc-900">
                  <span className="inline-flex items-center gap-1.5 group-hover:text-[#DD0000] transition">
                    {MODELLTESTS.length} module <ArrowRight size={14} className="transition group-hover:translate-x-1" />
                  </span>
                  <span className="text-[11px] text-zinc-400">{done}/{MODELLTESTS.length} të bëra</span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
};

// ---------------------------------------------------------------- Modul 1–15 of one Teil
export const ProjektLesenModulePage = () => {
  const { teil: teilNo } = useParams();
  const { data: attempts, isLoading, error, refetch } = useAttempts();
  const best = useMemo(() => bestByTeil(attempts), [attempts]);
  const def = LESEN_TEILE.find((t) => t.teil === Number(teilNo));

  if (!def) {
    return (
      <DashboardLayout>
        <div className="mx-auto max-w-[1440px] p-6">
          <ErrorState message="Ky Teil nuk ekziston." />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-[1440px] space-y-6 px-1 pb-10 font-sans sm:px-3">
        <PageHeader
          icon={BookOpen}
          badge="Goethe-Zertifikat B2 · Lesen"
          title={`Teil ${def.teil}`}
          description={`${def.label} · Aufgaben ${def.first}–${def.last} · ca. ${def.minutes} Min.`}
        >
          <Link
            to="/exam/b2/lesen"
            className="inline-flex items-center gap-1.5 text-xs font-bold text-zinc-700 hover:text-[#DD0000] transition"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Të gjitha Teil-et
          </Link>
        </PageHeader>

        {error && <ErrorState className="mb-4" message="Rezultatet e mëparshme nuk u ngarkuan." onRetry={refetch} />}

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {MODELLTESTS.map((mt) => {
            const t = mt.teile.find((x) => x.teil === def.teil);
            return (
              <Link key={mt.id} to={modulPath(def.teil, mt.number)} className={cardClass}>
                <div className="flex items-center justify-between gap-2">
                  <h2 className="text-base font-extrabold text-zinc-900 group-hover:text-[#DD0000] transition">Modul {mt.number}</h2>
                  {isLoading ? <Skeleton className="h-4 w-8" /> : <ScoreBadge attempt={best[`${mt.number}-${def.teil}`]} />}
                </div>
                <div className="mt-3 flex items-center justify-between text-[11px] font-bold text-zinc-400">
                  <span>S. {t.bookPages.join('–')}</span>
                  {!t.gradable && (
                    <AlertTriangle className="h-4 w-4 text-amber-500" title="Pa çelës të verifikuar" />
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
};

// ---------------------------------------------------------------- one Teil
const newAttemptId = () =>
  window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;

// Scans are 863×1220 px; `crop` shows only the [top, bottom] fraction of the page.
const PAGE_RATIO = 1220 / 863;

const SourcePage = ({ page, crop }) => {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50 p-4 text-xs text-amber-900">
        Faqja {page} e PDF-së nuk u gjet.
      </div>
    );
  }
  // On phones the scan is small: tapping opens the full-resolution page for pinch-zoom.
  return (
    <a href={pageImageUrl(page)} target="_blank" rel="noopener noreferrer" className="block" aria-label={`Hap faqen ${page} në madhësi të plotë`}>
      <div
        className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-2xs"
        style={crop ? { aspectRatio: `1 / ${PAGE_RATIO * (crop[1] - crop[0])}` } : undefined}
      >
        <img
          src={pageImageUrl(page)}
          alt={`${SOURCE_BOOK.title}, PDF faqja ${page}`}
          loading="lazy"
          onError={() => setFailed(true)}
          className="block h-auto w-full"
          style={crop ? { marginTop: `-${crop[0] * PAGE_RATIO * 100}%` } : undefined}
        />
      </div>
      <span className="mt-1 block text-center text-[11px] text-zinc-500 lg:hidden">Prek për ta zmadhuar</span>
    </a>
  );
};

const STATUS_TEXT = {
  correct: 'text-emerald-700',
  incorrect: 'text-red-700',
  unanswered: 'text-zinc-500',
  unverified: 'text-amber-700',
};

const STATUS_STYLE = {
  correct: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  incorrect: 'border-red-200 bg-red-50 text-red-800',
  unanswered: 'border-zinc-200 bg-zinc-50 text-zinc-600',
  unverified: 'border-amber-200 bg-amber-50 text-amber-900',
};

export const ProjektLesenTeilPage = () => {
  const { test, teil: teilNo } = useParams();
  const navigate = useNavigate();
  const teil = getTeil(test, teilNo);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [filter, setFilter] = useState('all');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const attemptId = useRef(newAttemptId());
  const { data: attempts } = useAttempts();

  if (!teil) {
    return (
      <DashboardLayout>
        <div className="mx-auto max-w-[1440px] p-6">
          <ErrorState message="Ky ushtrim nuk ekziston." />
        </div>
      </DashboardLayout>
    );
  }

  const answered = teil.questions.filter((q) => answers[q.id]).length;
  const previous = (attempts || []).filter(
    (a) => a.details.modelltest === teil.test && a.details.teil === teil.teil
  );
  const nextModul = teil.test < MODELLTESTS.length ? modulPath(teil.teil, teil.test + 1) : null;

  const submit = async () => {
    if (result || saving) return;
    const graded = gradeTeil(teil, answers);
    setResult(graded);
    setSaving(true);
    setSaveError(null);
    try {
      await projektExamAPI.submitTeil({
        attemptId: attemptId.current,
        test: teil.test,
        teil: teil.teil,
        answers,
        result: graded,
      });
    } catch (e) {
      console.error('Saving attempt failed:', e);
      setSaveError('Rezultati nuk u ruajt. Kontrollo lidhjen dhe provo përsëri.');
    } finally {
      setSaving(false);
    }
  };

  const retrySave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await projektExamAPI.submitTeil({
        attemptId: attemptId.current,
        test: teil.test,
        teil: teil.teil,
        answers,
        result,
      });
    } catch {
      setSaveError('Rezultati nuk u ruajt.');
    } finally {
      setSaving(false);
    }
  };

  const restart = () => {
    setAnswers({});
    setResult(null);
    setFilter('all');
    setSaveError(null);
    attemptId.current = newAttemptId();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const rows = result
    ? result.rows.filter((r) => filter === 'all' || r.status === 'incorrect' || r.status === 'unanswered')
    : [];

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-[1440px] space-y-6 px-1 pb-10 font-sans sm:px-3">
        {/* Navigation Breadcrumb */}
        <div className="flex flex-wrap items-center gap-2 text-xs font-bold text-zinc-400">
          <Link to="/exam/b2/lesen" className="text-zinc-600 hover:text-[#DD0000] transition">
            Lesen
          </Link>
          <span>/</span>
          <Link
            to={teilPath(teil.teil)}
            className="inline-flex items-center gap-1 text-zinc-600 hover:text-[#DD0000] transition"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Teil {teil.teil}
          </Link>
          <span>/</span>
          <span className="text-zinc-900">Modul {teil.test}</span>
        </div>

        {/* Header Bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-200/80 pb-4">
          <div>
            <h1 className="text-xl font-extrabold text-zinc-900 sm:text-2xl">
              Lesen Teil {teil.teil} · Modul {teil.test}
            </h1>
            <p className="text-xs text-zinc-500 mt-0.5">{teil.label}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="rounded-lg border border-zinc-200 bg-white px-2.5 py-1 text-xs font-bold text-zinc-700">
              ca. {teil.minutes} Min.
            </span>
            <span className="rounded-lg border border-zinc-200 bg-white px-2.5 py-1 text-xs font-bold text-zinc-700">
              S. {teil.bookPages.join('–')}
            </span>
          </div>
        </div>

        {!teil.gradable && (
          <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 p-3.5 text-xs text-amber-900 font-medium">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            Për këtë pjesë nuk ka ende çelës përgjigjesh të verifikuar. Përgjigjet ruhen, por nuk vlerësohen automatikisht.
          </div>
        )}

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 lg:items-start">
          {/* Left: reading text pages */}
          <div className="space-y-4">
            {teil.textPages.map((p) => (
              <SourcePage key={`${p.page}-${p.crop?.[0] ?? 'all'}`} page={p.page} crop={p.crop} />
            ))}
          </div>

          {/* Right: answer sheet, scrolling on its own next to the text */}
          <div className="space-y-4 lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto">
          {/* Answer sheet laid out like the official Goethe-Zertifikat B2 Antwortbogen */}
          <div className="border-2 border-zinc-900 bg-white text-zinc-900">
            <div className="flex items-stretch border-b-2 border-zinc-900">
              <div className="flex items-center bg-zinc-900 px-3 text-sm font-black tracking-widest text-white">LESEN</div>
              <div className="flex-1 px-3 py-2">
                <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Goethe-Zertifikat B2</p>
                <p className="text-sm font-extrabold">Antwortbogen</p>
              </div>
              <div className="flex items-center px-3 text-xs font-bold text-zinc-600" aria-live="polite">
                {result
                  ? `${result.correct}/${result.gradedTotal} saktë${result.score == null ? '' : ` · ${result.score}%`}`
                  : `${answered}/${teil.questions.length}`}
              </div>
            </div>

            <div className="p-4">
              <div className="mb-3 flex items-center justify-between gap-2">
                <span className="border-2 border-zinc-900 px-2 py-0.5 text-xs font-black">Teil {teil.teil}</span>
                <span className="flex items-center gap-1.5 text-[10px] text-zinc-500">
                  Markieren Sie so:
                  <span className="flex h-4 w-4 items-center justify-center border-2 border-zinc-800 text-[10px] font-black leading-none text-zinc-900">✕</span>
                </span>
              </div>

              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th aria-hidden="true" />
                    {teil.options.map((opt) => (
                      <th key={opt} scope="col" className="pb-1 text-center text-xs font-bold">{opt}</th>
                    ))}
                    <th aria-hidden="true" />
                  </tr>
                </thead>
                <tbody>
                  {teil.questions.map((q) => {
                    const row = result?.rows.find((r) => r.id === q.id);
                    return (
                      <tr key={q.id} className="border-t border-zinc-200">
                        <th scope="row" className="w-8 py-1.5 pr-2 text-left text-sm font-black">{q.number}</th>
                        {q.options.map((opt) => {
                          const selected = answers[q.id] === opt;
                          const isCorrect = result && q.verified && q.correct === opt;
                          return (
                            <td key={opt} className="py-1.5 text-center">
                              <label
                                aria-label={`Aufgabe ${q.number}: ${opt}`}
                                className={cn(
                                  'mx-auto flex h-7 w-7 items-center justify-center border-2 text-sm font-black leading-none transition',
                                  result ? 'cursor-default' : 'cursor-pointer hover:bg-zinc-100',
                                  'border-zinc-800 bg-white text-zinc-900',
                                  isCorrect && 'border-emerald-700 bg-emerald-600 text-white hover:bg-emerald-600',
                                  result && selected && row?.status === 'incorrect' && 'border-red-700 bg-red-600 text-white hover:bg-red-600'
                                )}
                              >
                                <input
                                  type="radio"
                                  name={q.id}
                                  value={opt}
                                  checked={selected}
                                  disabled={Boolean(result)}
                                  onChange={() => setAnswers((a) => ({ ...a, [q.id]: opt }))}
                                  className="sr-only"
                                />
                                {selected ? '✕' : ''}
                              </label>
                            </td>
                          );
                        })}
                        <td className={cn('w-12 pl-1 text-right text-xs font-bold', row && STATUS_TEXT[row.status])}>
                          {row?.status === 'correct' && '✓'}
                          {row?.status === 'incorrect' && `✗ ${q.correct}`}
                          {row?.status === 'unanswered' && `– ${q.correct}`}
                          {row?.status === 'unverified' && '?'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {!result && (
                <button
                  type="button"
                  className="mt-5 h-11 w-full bg-zinc-950 font-bold text-xs text-white hover:bg-[#DD0000] transition"
                  onClick={submit}
                  disabled={saving}
                >
                  Dorëzo Përgjigjet
                </button>
              )}

              {result && (
                <div className="mt-4 space-y-2 text-xs">
                  {saving && <p className="text-zinc-400">Duke ruajtur…</p>}
                  {saveError && <ErrorState message={saveError} onRetry={retrySave} />}
                </div>
              )}
            </div>
          </div>
          </div>
        </div>

        {/* Results Card */}
        {result && (
          <div className="overflow-hidden rounded-2xl border border-zinc-200/80 bg-white p-6 shadow-sm space-y-4">
            <h2 className="text-base font-extrabold text-zinc-900">Rezultati i Testit</h2>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
              {[
                ['Të sakta', result.correct, 'text-emerald-700'],
                ['Të gabuara', result.incorrect, 'text-red-700'],
                ['Pa përgjigje', result.unanswered, 'text-zinc-600'],
                ['Pa çelës', result.unverified, 'text-amber-700'],
                ['Pikët', result.score == null ? '—' : `${result.score}%`, 'text-zinc-950'],
              ].map(([label, value, cls]) => (
                <div key={label} className="rounded-xl border border-zinc-200/70 bg-[#fafafa] p-3 text-center">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">{label}</p>
                  <p className={cn('text-xl font-black mt-1', cls)}>{value}</p>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              <button
                type="button"
                className={`rounded-xl px-3.5 py-1.5 text-xs font-bold transition ${
                  filter === 'all'
                    ? 'bg-zinc-950 text-white'
                    : 'border border-zinc-200 bg-white text-zinc-700'
                }`}
                onClick={() => setFilter('all')}
              >
                Të gjitha përgjigjet
              </button>
              <button
                type="button"
                className={`rounded-xl px-3.5 py-1.5 text-xs font-bold transition ${
                  filter === 'wrong'
                    ? 'bg-zinc-950 text-white'
                    : 'border border-zinc-200 bg-white text-zinc-700'
                }`}
                onClick={() => setFilter('wrong')}
              >
                Vetëm të gabuarat
              </button>
            </div>

            <ul className="space-y-1.5">
              {rows.length === 0 && <li className="text-xs text-zinc-400">Asnjë përgjigje e gabuar.</li>}
              {rows.map((r) => (
                <li
                  key={r.id}
                  className={cn(
                    'flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border p-3 text-xs',
                    STATUS_STYLE[r.status]
                  )}
                >
                  <span className="w-20 font-bold">Aufgabe {r.number}</span>
                  <span>
                    Përgjigjja jote: <b className="uppercase">{r.given || '—'}</b>
                  </span>
                  <span>
                    E sakta: <b className="uppercase">{r.verified ? r.correct : 'e paverifikuar'}</b>
                  </span>
                  <span className="ml-auto inline-flex items-center gap-1 font-bold">
                    {r.status === 'correct' && (
                      <>
                        <CheckCircle2 className="h-4 w-4" /> Saktë
                      </>
                    )}
                    {r.status === 'incorrect' && (
                      <>
                        <XCircle className="h-4 w-4" /> Gabim
                      </>
                    )}
                    {r.status === 'unanswered' && 'Pa përgjigje'}
                    {r.status === 'unverified' && 'Pa vlerësim'}
                  </span>
                </li>
              ))}
            </ul>

            <div className="flex flex-wrap gap-2 pt-3">
              <button
                type="button"
                onClick={restart}
                className="inline-flex items-center gap-1.5 rounded-xl border border-zinc-200 bg-white px-4 py-2.5 text-xs font-bold text-zinc-700 hover:bg-zinc-50"
              >
                <RotateCw className="h-3.5 w-3.5" /> Përsërit Testin
              </button>
              {nextModul && (
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 rounded-xl bg-zinc-950 px-4 py-2.5 text-xs font-bold text-white hover:bg-[#DD0000] transition"
                  onClick={() => {
                    restart();
                    navigate(nextModul);
                  }}
                >
                  Moduli Tjetër <ArrowRight className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};
